import { useMemo } from 'react';
import { X } from 'lucide-react';
import MathText from '../VueQuestion/MathText';
import ChoixAttendu from './ChoixAttendu';
import PastilleRubrique from './PastilleRubrique';
import PastilleNiveau from './PastilleNiveau';

/**
 * Sélecteur d'attendus du wizard « Coller mes notes » quand on colle une NOTE
 * SEULE : « Ajouter un attendu… » ouvre le même menu que les colonnes de
 * compétences (ChoixAttendu : recherche par mots ou Thème → Chapitre, depuis
 * 2026-09-29, à la place des deux listes et des cases à cocher). Chaque
 * attendu retenu devient une CARTE portant ses propres compétences (comp_ids
 * est par exercice côté back), Calculer/Lire pré-cochée. Attendus du programme
 * uniquement (pas la branche legacy savoirs-faires — décision 2026-08-30).
 *
 * @param {Array} themes  réponse de useTheme(niveau)
 * @param {Array} attendus  aplat du niveau (attendusDuNiveau), repli de la recherche
 * @param {Array} selectionnes  [{id, description, compIds}]
 * @param {Function} onToggle  (attendu) => void
 * @param {Array} competences  [{id, nom}] de la matière
 * @param {Function} onToggleComp  (attenduId, compId) => void
 */
const SelecteurAttendusCompact = ({
    themes = [], attendus = [], selectionnes = [], onToggle, competences = [], onToggleComp,
    niveauClasse = '',
}) => {
    const idsSelectionnes = useMemo(() => new Set(selectionnes.map((a) => a.id)), [selectionnes]);

    // Ajouter seulement : re-choisir un attendu déjà retenu ne le retire pas
    // (la croix de sa carte le fait).
    const ajouter = (a) => {
        if (!idsSelectionnes.has(a.id)) onToggle(a);
    };

    return (
        <div className="space-y-3">
            <ChoixAttendu
                valeur={null}
                onChange={ajouter}
                themes={themes}
                attendus={attendus}
                libelleVide="Ajouter un attendu…"
                niveauClasse={niveauClasse}
            />

            {selectionnes.length > 0 && (
                <div className="space-y-2">
                    {selectionnes.map((a) => (
                        <div key={a.id} className="border-2 border-blue-100 bg-blue-50/50 rounded-xl px-3 py-2">
                            <div className="flex items-start gap-2">
                                <span className="flex-1 text-[13px] font-bold text-blue-900 leading-snug">
                                    <MathText>{a.description}</MathText>
                                </span>
                                <PastilleNiveau attendu={a} niveauClasse={niveauClasse} />
                                <PastilleRubrique attendu={a} />
                                <button
                                    type="button"
                                    onClick={() => onToggle(a)}
                                    className="w-5 h-5 rounded-full bg-blue-200 hover:bg-blue-300 flex items-center justify-center flex-shrink-0"
                                    title="Retirer cet attendu"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                            {/* Compétences travaillées PAR cet attendu */}
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {competences.map((c) => {
                                    const coche = (a.compIds || []).includes(c.id);
                                    return (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => onToggleComp(a.id, c.id)}
                                            className={`px-2 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${
                                                coche
                                                    ? 'bg-blue-600 border-blue-600 text-white'
                                                    : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300'
                                            }`}
                                        >
                                            {c.nom}
                                        </button>
                                    );
                                })}
                            </div>
                            {(a.compIds || []).length === 0 && (
                                <p className="text-[11px] font-bold text-amber-600 mt-1">Cochez au moins une compétence.</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SelecteurAttendusCompact;
